import { store } from 'slices';
import { Feature } from './utils';

export const ROLE_ADMIN = 'ROLE_ADMIN';

export type PermissionItem = {
  feature?: string;
  permissionMask?: number;
  permission?: number;
};

type AccountPermissionState = {
  permissions?: PermissionItem[];
  authorities?: string[];
};

type PermissionMaskMap = Partial<Record<Feature, number>>;

let lastPermissionListRef: PermissionItem[] | null = null;
let lastPermissionMaskMap: PermissionMaskMap = {};

export const hasAdminAuthority = (authorities?: string[] | null): boolean =>
  Array.isArray(authorities) && authorities.includes(ROLE_ADMIN);

export const isAdminAccount = (account?: AccountPermissionState | null): boolean =>
  hasAdminAuthority(account?.authorities);

const toPermissionList = (account?: AccountPermissionState | null): PermissionItem[] => {
  const permissions = account?.permissions;

  if (Array.isArray(permissions)) return permissions;

  return [];
};

const toPermissionMask = (item?: PermissionItem) =>
  Number(item?.permissionMask ?? item?.permission ?? 0);

export const hasPermissionMask = (permissionMask?: number, requiredPermission?: number): boolean => {
  if (!requiredPermission) {
    return false;
  }

  return (Number(permissionMask || 0) & requiredPermission) === requiredPermission;
};

const buildPermissionMaskMap = (permissions: PermissionItem[] | undefined): PermissionMaskMap => {
  const permissionList = Array.isArray(permissions) ? permissions : [];
  const permissionMaskMap: PermissionMaskMap = {};

  permissionList.forEach((item) => {
    const feature = item?.feature as Feature | undefined;
    if (!feature) {
      return;
    }

    const permissionMask = toPermissionMask(item);
    permissionMaskMap[feature] = permissionMask;
  });

  return permissionMaskMap;
};

const getPermissionMaskMap = (account?: AccountPermissionState | null): PermissionMaskMap => {
  const permissions = toPermissionList(account);

  if (permissions === lastPermissionListRef) {
    return lastPermissionMaskMap;
  }

  lastPermissionListRef = permissions;
  lastPermissionMaskMap = buildPermissionMaskMap(permissions);

  return lastPermissionMaskMap;
};

export const hasFeaturePermission = (feature?: Feature, requiredPermission?: number): boolean => {
  const account = store.getState().login.account;
  const permissionMaskMap = getPermissionMaskMap(account);

  if (isAdminAccount(account)) {
    return true;
  }

  if (!feature || !requiredPermission) {
    return false;
  }

  return hasPermissionMask(permissionMaskMap[feature], requiredPermission);
};

export const createFeaturePermissionChecker = (feature: Feature) =>
  (requiredPermission?: number) => hasFeaturePermission(feature, requiredPermission);

export default hasFeaturePermission;
